import { Checkbox, Group, Loader, Pagination, ScrollArea, Table, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DataPanel } from './DataPanel';

interface Column<T> {
    key: string;
    header: string;
    render: (row: T) => ReactNode;
}

interface PaginatedTableProps<T> {
    columns: Column<T>[];
    rows: T[];
    total: number;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    isLoading?: boolean;
    onRowClick?: (row: T) => void;
    rowKey: (row: T) => string;
    emptyMessage?: string;
    /** Opt-in row selection. Omit for a plain table. */
    selection?: {
        selectedIds: string[];
        onChange: (ids: string[]) => void;
    };
}

export function PaginatedTable<T>({
    columns,
    rows,
    total,
    page,
    pageSize,
    onPageChange,
    isLoading,
    onRowClick,
    rowKey,
    emptyMessage,
    selection,
}: PaginatedTableProps<T>) {
    const { t } = useTranslation();
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const selected = new Set(selection?.selectedIds ?? []);
    const pageIds = rows.map(rowKey);
    const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
    const someOnPageSelected = pageIds.some((id) => selected.has(id)) && !allOnPageSelected;
    const columnCount = columns.length + (selection ? 1 : 0);

    /** Select-all toggles only the current page, leaving off-page picks intact. */
    const togglePage = () => {
        if (!selection) {
            return;
        }
        const next = new Set(selected);
        if (allOnPageSelected) {
            pageIds.forEach((id) => next.delete(id));
        } else {
            pageIds.forEach((id) => next.add(id));
        }
        selection.onChange([...next]);
    };

    const toggleRow = (id: string) => {
        if (!selection) {
            return;
        }
        const next = new Set(selected);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        selection.onChange([...next]);
    };

    return (
        <DataPanel p={0}>
            {isLoading ? (
                <Group p="lg">
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">
                        {t('common.loading.default')}
                    </Text>
                </Group>
            ) : null}
            <ScrollArea>
                <Table stickyHeader>
                    <Table.Thead>
                        <Table.Tr>
                            {selection ? (
                                <Table.Th w={44}>
                                    <Checkbox
                                        aria-label={t('common.actions.selectAll')}
                                        checked={allOnPageSelected}
                                        indeterminate={someOnPageSelected}
                                        onChange={togglePage}
                                    />
                                </Table.Th>
                            ) : null}
                            {columns.map((col) => (
                                <Table.Th key={col.key}>{col.header}</Table.Th>
                            ))}
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {!isLoading && rows.length === 0 ? (
                            <Table.Tr>
                                <Table.Td colSpan={columnCount}>
                                    <Text ta="center" c="dimmed" py="xl" size="sm">
                                        {emptyMessage ?? t('common.pagination.noRecords')}
                                    </Text>
                                </Table.Td>
                            </Table.Tr>
                        ) : null}
                        {rows.map((row) => (
                            <Table.Tr
                                key={rowKey(row)}
                                style={onRowClick ? { cursor: 'pointer' } : undefined}
                                onClick={() => onRowClick?.(row)}
                            >
                                {selection ? (
                                    <Table.Td
                                        style={{ verticalAlign: 'top' }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Checkbox
                                            aria-label={t('common.actions.select')}
                                            checked={selected.has(rowKey(row))}
                                            onChange={() => toggleRow(rowKey(row))}
                                        />
                                    </Table.Td>
                                ) : null}
                                {columns.map((col) => (
                                    <Table.Td key={col.key} style={{ verticalAlign: 'top' }}>
                                        {col.render(row)}
                                    </Table.Td>
                                ))}
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </ScrollArea>
            <Group justify="space-between" p="md" pt="sm">
                <Text size="sm" c="dimmed">
                    {t('common.pagination.total', { count: total })}
                </Text>
                <Pagination value={page} onChange={onPageChange} total={totalPages} />
            </Group>
        </DataPanel>
    );
}
